<template>
    <div class="all-saved-reports">
        <h3>{{ title }}</h3>

        <div class="saved-reports">
            <div id="search-container">
                <ds-input-field
                    id="search-input"
                    v-model="value"
                    label="Search saved reports"
                    @keyup="searchReportNameMatches"
                >
                    <template #leading>
                        <ds-icon name="search" />
                    </template>
                </ds-input-field>
            </div>

            <reports-filter
                :all-reports="reportsObject"
                @filteredSavedReports="filteredReports"
                @allFilteredReports="allFilteredReports"
            />
        </div>

        <div id="report-count">
            <span id="report-count-span">{{ reportCountUpdated }} </span>
            <span id="report-results">results</span>
        </div>

        <div v-if="hasSavedReports">
            <div id="report-table">
                <ds-table-grid
                    v-if="!noSearchResults"
                    ref="table"
                    v-model="selectedReports"
                    :data="paginatedData"
                    selectable
                    :infinite="infinite"
                    :select-all.sync="selectAll"
                    :selected-options="selectedOptions"
                    :sort-ascending="sortAscending"
                    :sort-field="sortField"
                    :limit="limit"
                    :show-loading="loading"
                    :total="total"
                    :click-row="(row,event) => openReport(row, event)"
                    @update:selectAll="updateAllSelected"
                    @load="load"
                    @sort="sortTable"
                    @action="handleAction"
                    @input="handleInputChange"
                >
                    <ds-table-grid-column
                        label="Report Name"
                        width="300"
                        prop="reportName"
                        sortable
                    >
                        <template slot-scope="props">
                            <!-- eslint-disable vue/no-v-html -->
                            <span class="report-name" v-html="props.reportName" />
                            <!-- eslint-enable vue/no-v-html -->
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Report category"
                        prop="reportCategoryType"
                        width="150"
                        sortable
                    />

                    <ds-table-grid-column
                        label="Report template"
                        prop="reportTemplate"
                        width="150"
                        sortable
                    />

                    <ds-table-grid-column
                        label="Date created"
                        width="200"
                        prop="dateCreated"
                        sortable
                    >
                        <template slot-scope="props">
                            {{ dateFormat(props.dateCreated, 'MMM D, YYYY: h:mm a') }}
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        v-if="isCreatedByEnabled"
                        label="Created by"
                        width="200"
                        prop="createdBy"
                        sortable
                    />

                    <ds-table-grid-column
                        v-if="isPinned"
                        width="60"
                        class="pinned"
                    >
                        <template slot-scope="props">
                            <ds-dropdown class="dropdown pinned" @click.stop>
                                <ds-icon-button class="icon-only">
                                    <ds-icon name="more-vertical" />
                                </ds-icon-button>

                                <div slot="menu">
                                    <ul class="dropdown-menu-list pin-to-top">
                                        <li
                                            class="dropdown-menu-item"
                                            @click="pinToTop(props.reportName)"
                                        >
                                            <img src="/resources/funnel/images/svg/pin-icon.svg" alt="pin-image" />
                                            <div>Pin to top</div>
                                        </li>
                                        <li
                                            class="dropdown-menu-item"
                                            @click="viewReport(props)"
                                        >
                                            <ds-icon name="external-link" />
                                            <div>Open report</div>
                                        </li>
                                    </ul>
                                </div>
                            </ds-dropdown>
                        </template>
                    </ds-table-grid-column>
                </ds-table-grid>

                <div v-else>
                    <h4>No results found for {{ value }}</h4>
                </div>
            </div>
        </div>

        <div v-else class="no-saved-reports">
            <h2>You do not have any saved reports</h2>

            <p>
                Saved reports and searches are dynamic lists that can be referenced quickly. you can create a new saved search by cloning an existing report.
                <a href="https://learn.thryv.com/hc/en-us/articles/40579235477773-Import-contacts-into-Keap" target="_blank">
                    Learn More
                </a>
            </p>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import ReportsFilter from './ReportsFilter.vue';

export default {
    components: {
        ReportsFilter,
    },

    props: {
        title: String,
        reportCount: Number,
        reportsObject: Array,
        isCreatedByEnabled: {
            type: Boolean,
            default: false,
        },
        isPinned: Boolean,
    },

    data() {
        return {
            value: '',
            options: [
                { value: 'delete', label: 'Delete report(s)' },
            ],
            selectedOptions: [{
                label: 'Delete Report(s)',
                value: 'deleteReports',
                className: 'negative',
            }],
            selectedReports: [],
            reportCountUpdated: this.reportCount,
            selectAll: false,
            sortAscending: false,
            sortField: 'dateCreated',
            paginatedData: this.reportsObject,
            reportsObjectLocal: this.reportsObject,
            loading: false,
            offset: 0,
            limit: 10,
            filledData: true,
            total: 0,
            infinite: true,
            allReportsList: [],
            categoryItems: [],
        };
    },

    async created() {
        await this.loadPropsData();
    },

    watch: {
        reportsObject() {
            this.loadPropsData();
        },
    },

    computed: {
        hasSavedReports() {
            return Boolean(this.reportsObjectLocal?.length);
        },

        noSearchResults() {
            return this.value && !this.paginatedData.length;
        },
    },

    methods: {
        loadPropsData() {
            this.reportCountUpdated = this.reportCount;
            this.paginatedData = this.reportsObject;
            this.reportsObjectLocal = this.reportsObject;

            this.paginatedData = [];

            if (this.reportsObjectLocal) {
                this.offset = 0;
                const dataInternal = this.reportsObjectLocal.slice(this.offset, this.limit + this.offset);

                this.paginatedData = dataInternal;
                this.offset += dataInternal.length;
                this.reportsObjectLocalNotSliced = this.reportsObjectLocal;
            }
        },

        load() {
            const dataInternal = this.reportsObjectLocal.slice(this.offset, this.limit + this.offset);

            this.loading = true;
            setTimeout(() => {
                if (dataInternal.length < this.limit || this.reportsObjectLocal.length <= this.limit) {
                    this.$refs.table.finishLoading();
                }

                this.paginatedData.push(...dataInternal);
                this.offset += dataInternal.length;
                this.loading = false;
            }, 300);
        },

        handleAction(value) {
            if (value === 'deleteReports') {
                this.deleteSavedReports();
            }
        },

        updateAllSelected(value) {
            if (value) {
                this.total = this.selectedReports.length;
                this.$refs.table.selectedRows = [];
                this.selectedReports = this.reportsObjectLocal;
            } else {
                this.selectedReports = [];
                this.total = 0;
                this.$refs.table.resetSelections();
            }
        },

        handleInputChange() {
            if (this.selectedReports < this.reportsObjectLocal) {
                const selectedItems = [];

                for (let i = 0; i < this.reportsObjectLocal.length; i++) {
                    if (this.$refs.table.isSelected(i)) {
                        selectedItems.push(this.reportsObjectLocal[i]);
                    }
                }
                this.selectedReports = selectedItems;
            }
        },

        deleteSavedReports() {
            const reportIds = [];
            let reportsObjectLocalNotSlicedUpdated = [];
            let reportsObjectsUpdated = [];

            if (this.selectedReports.length === 0) {
                this.$toast({ message: 'Please select at least one report to delete.' });

                return;
            }

            this.selectedReports.filter((selectedReport) => reportIds.push(selectedReport.id));

            reportsObjectLocalNotSlicedUpdated = this.reportsObjectLocalNotSliced.filter((reportFilter) => !reportIds.includes(reportFilter.id));
            reportsObjectsUpdated = this.reportsObjectLocal.filter((reportFilter) => !reportIds.includes(reportFilter.id));

            const xhr = new XMLHttpRequest();

            xhr.open('DELETE', `/app/report/removeSavedReports/${reportIds}`);
            xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
            xhr.responseType = 'json';

            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHttpRequest.DONE) {
                    if (xhr.status === 200) {
                        this.successMessage();
                        this.reportsObjectLocal = reportsObjectsUpdated;
                        this.reportsObjectLocalNotSliced = reportsObjectLocalNotSlicedUpdated;
                        this.paginatedData = [];
                        this.paginatedData = this.reportsObjectLocal.slice(0, this.offset);
                        this.reportCountUpdated = this.reportsObjectLocal.length;
                        this.$refs.table.noMoreData = this.reportCountUpdated <= this.limit;
                        this.$refs.table.resetSelections();
                    } else {
                        this.failMessage();
                    }
                }
            };
            xhr.send();
            this.$emit('isDeletePinnedReport');
        },

        sortTable({ field, ascending }) {
            const factor = ascending === true ? -1 : 1;

            this.sortAscending = ascending;

            if (field === 'dateCreated') {
                this.reportsObjectLocal = this.reportsObjectLocal.sort((prev, next) => {
                    if (prev.dateCreated > next.dateCreated) return -1 * factor;
                    if (prev.dateCreated < next.dateCreated) return 1 * factor;

                    return 0;
                });
                this.paginatedData = [];
                this.paginatedData = this.reportsObjectLocal.slice(0, this.offset);

                if (this.reportsObjectLocal.length === this.reportsObjectLocalNotSliced.length) {
                    this.reportsObjectLocalNotSliced = this.reportsObjectLocal;
                }
            } else {
                this.reportsObjectLocal.sort((a, b) => {
                    let sort;

                    if (a[field] < b[field]) {
                        sort = -1;
                    } else if (a[field] > b[field]) {
                        sort = 1;
                    } else {
                        sort = 0;
                    }

                    return sort * factor;
                });
                this.paginatedData = [];
                this.paginatedData = this.reportsObjectLocal.slice(0, this.offset);

                if (this.reportsObjectLocal.length === this.reportsObjectLocalNotSliced.length) {
                    this.reportsObjectLocalNotSliced = this.reportsObjectLocal;
                }
            }
        },

        searchReportNameMatches() {
            const inputValue = this.value;
            let reportsArray = [];

            if (this.categoryItems.length) {
                for (let i = 0; i < this.allReportsList.length; i++) {
                    const report = this.allReportsList[i];

                    if (report.reportName.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0 || (this.isCreatedByEnabled && report?.createdBy?.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0)) {
                        reportsArray.push(report);
                    }
                }
            } else {
                reportsArray = this.reportsObjectLocalNotSliced;
            }

            this.reportsObjectLocal = reportsArray;
            this.paginatedData = [];
            this.paginatedData = this.reportsObjectLocal.filter(({ reportName }) => {
                return reportName?.toLowerCase().startsWith(inputValue.toLowerCase());
            });
            this.reportCountUpdated = this.paginatedData.length;
        },

        successMessage() {
            this.$toast({ message: 'Report(s) successfully removed.' });
        },

        failMessage() {
            this.$toast({ message: 'Failed to remove report(s). Please reload the page' });
        },

        dateFormat(date, format = 'll') {
            return moment(date).isValid() ? moment(date).format(format) : '';
        },

        pinToTop(Request) {
            this.$emit('pinToTop', Request);
        },

        filteredReports(filteredReports, ReportsCount) {
            this.paginatedData = filteredReports;
            this.reportsObjectLocal = filteredReports;
            this.reportCountUpdated = ReportsCount;
        },

        allFilteredReports(categoryItems, filteredReportsList) {
            this.allReportsList = filteredReportsList;
            this.categoryItems = categoryItems;
        },

        openReport(report, event) {
            if (['td', 'span'].includes(event?.target?.localName)
            || ['numeric-cell', 'options-cell', 'cell'].includes(event?.target?.className)
            ) {
                this.$emit('openReport', report.reportUri);
            }
        },
        viewReport(report) {
            this.$emit('openReport', report.reportUri);
        },
    },

};
</script>

<style lang="scss">
#action-options > div > div > div.dropdown-menu.icons.open > div.menu > div > ul.dropdown-menu-list {
    padding: 0rem 0;
}

#action-options > div.button-dropdown > div.dropdown > div.dropdown-menu > div.menu > div > ul {
    margin: 0em 0;
    padding-left: 0px;
}

#action-options > div.button-dropdown > div.dropdown > div.dropdown-menu > div.menu > div > ul > li{
    padding-left: 24px;
}

#action-options > div > div > div.dropdown-reference > button {
    height: 32px;
    width: 92px;
    padding-left: 22px;
}

#search-input > input {
    width: 270px;
    height: 37px;
}

#search-input > div.leading > svg.icon {
    height: 16px;
    color: rgba(0, 0, 0, 0.6);
}

#report-table > div > div.table-wrapper > table > tbody > tr > td > div.cell {
    margin-top: 12px;
}

.saved-reports {
    display: flex;
    justify-content: space-between;
}
</style>

<style lang="scss" scoped>
#search-container{
    display:flex;

    #search-input {
        margin-top: 20px;
        width: 270px;
        height: 35px;
        margin-bottom: 25px;
    }
}

#report-count {
    width: fit-content;
    margin-top: 20px;
    margin-bottom: 20px;
}

#report-count-span {
    width: 20px;
    height: 32px;
    margin: 0 20px 0 0;
    font-size: 32px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1;
    letter-spacing: normal;
    text-align: left;
    color: #242433;
}

#report-results {
    width: 44px;
    height: 20px;
    margin-left: -22px;
    margin-top: 12px;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: normal;
    text-align: left;
    color: #858593;
}

h3 {
    font-size: 28px;
}

.no-saved-reports {
    padding: $spacing-400 0;
    text-align: center;
    p {
        width: px-to-rem(380px);
        margin: 0 auto;
    }
    h2 {
        font-weight: $font-weight-semibold;
        margin: $spacing-200 0 $spacing-200;
    }
}

.pin-to-top {
  margin-top: 0;
  margin-bottom: 0;
}

.pin-to-top div {
    margin-left: $spacing-200;
}

.selectable-row:hover{
  .report-name{
    text-decoration: underline;
  }
}

</style>
